import React from 'react';
import PropTypes, {
  arrayOf, bool, shape, func, string, number
} from 'prop-types';
import { useConfigService } from '@thd-nucleus/experience-context';
import { useDomPath } from '@thd-olt-functional/utils';
import { Fulfillment } from '@thd-olt-component-react/fulfillment';
import { ProductAddOns } from '@thd-olt-component-react/product-add-ons';

import { isMobile, parseStringConfig, shouldEnableProductAddOn } from '../../util';
import { BuyboxActions } from './BuyboxActions';
import { BuyboxMessage } from './BuyboxMessage';
import { track } from '../../track';

const BuyboxLiteQuantity = ({
  itemId,
  unavailable,
  store,
  covidAlert,
  channel,
  onATCClick,
  fulfillment,
  isATCDisabled,
  onFulfillmentChange,
  bopisHolidayTiming,
  hideInstantCheckout,
  hidePaypalCheckout,
  directData,
  quantityLimit,
  minimumOrderQuantity,
  configuratorData,
  subscription,
  currentSelectedFulfillment,
  configurableProductDetails,
  availableQuantity,
  showQuantity,
  customRisMessage,
  onQuantityChange,
  risMessageEligible,
  isRisMessageEnabled,
  onDeliveryOptionsZipCodeChange,
  hideProReferral,
  buyboxState,
  productInfo,
  onProductAddOnChange
}) => {
  const [pathForAdvisoryMessage] = useDomPath();
  const isMobileDevice = isMobile(channel);
  const { storeId, storeName, storeZip } = store;
  const {
    zipCode, isShowATC, quantity, fulfillmentMethod, availableFulfillments,
    isOutOfStockOnline, paypalEligible, minimumBopisQuantity,
    maximumBopisQuantity, isItemOOS, quantityLimitExceed, fallbackMode,
    productAddOns = {} // Provide default value to avoid destructure error
  } = buyboxState;
  const { dualPath, virtualAssistance } = productAddOns;
  const addOnProductList = useConfigService('AddOnProductList');
  const configValue = parseStringConfig(addOnProductList); // parse the string values of key and value
  const isProductAddOnEnable = shouldEnableProductAddOn(productInfo, configValue);

  return (
    <div
      // eslint-disable-next-line tailwindcss/no-arbitrary-value
      className={`sui-grid ${isMobileDevice ? 'sui-w-auto' : 'sui-w-full'}`}
      data-component="BuyboxLite"
    >
      <div className="sui-grid-cols-12">
        <div className="sui-mb-3 sui-bg-primary sui-overflow-hidden">
          <div>
            {!unavailable && (
              <Fulfillment
                itemId={itemId}
                covidAlert={covidAlert}
                onChange={onFulfillmentChange}
                onDeliveryZipCodeChange={onDeliveryOptionsZipCodeChange}
                bopisHolidayTiming={bopisHolidayTiming}
                directData={directData}
                isBuyBoxLite
                dualPath={dualPath}
                quantity={quantity}
                configurableProductDetails={configurableProductDetails}
                currentSelectedFulfillment={currentSelectedFulfillment}
              />
            )}
          </div>
          <div className="sui-mt-3">
            {
              !isOutOfStockOnline && isShowATC && !!minimumOrderQuantity && (
                <BuyboxMessage
                  onRender={() => {
                    if (pathForAdvisoryMessage.length) {
                      track('advisory-message', {
                        message: `This item can only be purchased in quantities of ${minimumOrderQuantity} or more`,
                        path: pathForAdvisoryMessage
                      });
                    }
                  }}
                  message={`This item can only be purchased in quantities of ${minimumOrderQuantity} or more`}
                  status="warning"
                />
              )
            }
            {quantityLimit > 0 && quantity > (quantityLimit - 1) && (
              <BuyboxMessage
                onRender={() => {
                  if (pathForAdvisoryMessage.length) {
                    track('advisory-message', {
                      message: `This item has a limit of ${quantityLimit} per order.`,
                      path: pathForAdvisoryMessage
                    });
                  }
                }}
                message={`This item has a limit of ${quantityLimit} per order.`}
                status="warning"
              />
            )}
            {
              !!(minimumBopisQuantity) && (
                <BuyboxMessage
                  message={
                    `This item has a minimum quantity of
                              ${minimumBopisQuantity} for Pickup at ${storeName}.`
                  }
                  status="info"
                />
              )
            }
            {
              !!(maximumBopisQuantity) && (
                <BuyboxMessage
                  message={
                    `This item has a maximum quantity of
                              ${maximumBopisQuantity} for Pickup at ${storeName}.`
                  }
                  status="info"
                />
              )
            }
            {isRisMessageEnabled
              && (customRisMessage ? (
                <BuyboxMessage message={customRisMessage} status="info" />
              ) : (
                risMessageEligible
                && !isItemOOS
                && isShowATC
                && !dualPath
                && !virtualAssistance && (
                  <BuyboxMessage message="This item is only returnable in store." status="info" />
                )
              ))}
          </div>

          {isProductAddOnEnable && (
            <div className="sui-mt-3 sui-py-2">
              <ProductAddOns
                itemId={itemId}
                fulfillment={fulfillmentMethod}
                onChange={onProductAddOnChange}
                availableFulfillments={availableFulfillments}
                hideProReferral={hideProReferral}
                defaultProps={productAddOns}
                isTwoTileEnabledFS
                zipCode={zipCode}
                hideProtectionPlan
                // Please do not remove the below prop isTwoTileEnabled={isTwoTileEnabledFS}
                // as ProductAddOns has no prop mapping for isTwoTileEnabledFS
                // and still uses isTwoTileEnabled
                isTwoTileEnabled
                isShowATC={isShowATC}
              />
            </div>
          )}

          {!isItemOOS && isShowATC && !dualPath && (
            <>
              {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
              <div className="sui-mt-3">
                <BuyboxActions
                  alterBrowserHistory
                  disableATC={isATCDisabled}
                  fulfillment={fulfillment}
                  itemId={itemId}
                  quantity={quantity}
                  storeId={storeId}
                  storeZip={storeZip}
                  channel={channel}
                  zipCode={zipCode}
                  onATCClick={onATCClick}
                  fallbackMode={fallbackMode}
                  subscription={subscription}
                  hideQuantity={!showQuantity}
                  quantityLimit={quantityLimit}
                  paypalEligible={paypalEligible}
                  onQuantityChange={onQuantityChange}
                  configuratorData={configuratorData}
                  availableQuantity={availableQuantity}
                  hidePaypalCheckout={hidePaypalCheckout}
                  fulfillmentMethod={fulfillmentMethod}
                  quantityLimitExceed={quantityLimitExceed}
                  hideInstantCheckout={hideInstantCheckout}
                  minimumOrderQuantity={minimumOrderQuantity}
                  minimumBopisQuantity={minimumBopisQuantity}
                  maximumBopisQuantity={maximumBopisQuantity}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

BuyboxLiteQuantity.propTypes = {
  itemId: string,
  unavailable: bool,
  covidAlert: bool,
  bopisHolidayTiming: number,
  isATCDisabled: bool,
  channel: string,
  store: shape({
    storeZip: string,
    storeName: string,
    storeId: string,
  }),
  fulfillment: shape({
    backordered: bool
  }),
  onFulfillmentChange: func,
  onDeliveryOptionsZipCodeChange: func,
  onATCClick: func,
  hideInstantCheckout: bool,
  hidePaypalCheckout: bool,
  /* eslint-disable-next-line react/forbid-prop-types */
  directData: PropTypes.objectOf(PropTypes.any),
  // eslint-disable-next-line react/forbid-prop-types
  configuratorData: arrayOf(PropTypes.any),
  quantityLimit: number,
  currentSelectedFulfillment: string,
  configurableProductDetails: PropTypes.shape({
    isSpecialOrder: bool,
    configuratorHideQuantity: bool,
    /* eslint-disable-next-line react/forbid-prop-types */
    configuratorData: PropTypes.objectOf(PropTypes.any),
    isConfigurableBlinds: bool
  }),
  availableQuantity: shape({
    pickup: number,
    delivery: number
  }),
  subscription: shape({
    discountPercentage: string
  }),
  showQuantity: bool,
  risMessageEligible: bool,
  isRisMessageEnabled: bool,
  customRisMessage: string,
  minimumOrderQuantity: number,
  onQuantityChange: func,
  hideProReferral: bool,
  buyboxState: shape({
    disableATC: bool,
    paypalEligible: bool,
    quantity: number,
    coverage: number,
    zipCode: string,
    isShowATC: bool,
    isItemOOS: bool,
    fulfillmentMethod: string,
    /* eslint-disable-next-line react/forbid-prop-types */
    availableFulfillments: arrayOf(PropTypes.any),
    addAdditional: bool,
    quantityExceeded: bool,
    showFencingMessage: bool,
    minimumBopisQuantity: number,
    maximumBopisQuantity: number,
    isOutOfStockOnline: bool,
    quantityLimitExceed: bool
  }),
  onProductAddOnChange: func,
  productInfo: shape({
    productDepartment: string,
    subClassNumber: string
  })
};

BuyboxLiteQuantity.defaultProps = {
  itemId: null,
  unavailable: false,
  covidAlert: false,
  store: {
    storeId: null,
    storeZip: null,
  },
  isATCDisabled: false,
  channel: 'desktop',
  fulfillment: null,
  onFulfillmentChange: /* istanbul ignore next */ () => { },
  onDeliveryOptionsZipCodeChange: /* istanbul ignore next */ () => { },
  onATCClick: /* istanbul ignore next */ () => { },
  bopisHolidayTiming: null,
  hideInstantCheckout: false,
  hidePaypalCheckout: false,
  directData: null,
  configuratorData: null,
  quantityLimit: 0,
  currentSelectedFulfillment: '',
  configurableProductDetails: {
    isSpecialOrder: false,
    configuratorHideQuantity: false,
    configuratorData: {},
    isConfigurableBlinds: false
  },
  availableQuantity: null,
  subscription: null,
  showQuantity: false,
  risMessageEligible: false,
  isRisMessageEnabled: false,
  customRisMessage: '',
  onQuantityChange: /* istanbul ignore next */ () => { },
  minimumOrderQuantity: 1,
  hideProReferral: false,
  buyboxState: {
    quantity: 1,
    zipCode: null,
    isItemOOS: false,
    isShowATC: false,
    paypalEligible: false,
    isOutOfStockOnline: false,
    minimumBopisQuantity: null,
    maximumBopisQuantity: null,
    quantityLimitExceed: false,
    availableFulfillments: {}
  },
  onProductAddOnChange: () => { },
  productInfo: {
    productDepartment: '',
    subClassNumber: ''
  }
};

export { BuyboxLiteQuantity };